import {
  Box,
  Button,
  Container,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Grid,
  GridItem,
  Heading,
  Input,
  Select,
  VStack,
  HStack,
  Divider,
  Text,
} from '@chakra-ui/react';
import { DatePicker } from '../../components/DriverForm/DatePicker';

import { useDriverFormController } from './DriverForm.controller';
import { Footer } from '../../components/Landing/Footer';
import Header from '../../components/DriverForm/Header';
import ColorSelect from '../../components/DriverForm/ColorSelect';
import { colorOptions } from '../../utils/colorOptions';
import CustomFileUploader from '../../components/CustomFileUploader/CustomFileUploader';
import DniFrontIcon from '../../icons/DniFront.svg';
import DniBackIcon from '../../icons/DniBack.svg';
import LicenseFrontIcon from '../../icons/LicenseFront.svg';
import LicenseBackIcon from '../../icons/LicenseBack.svg';
import CedulaFrontIcon from '../../icons/CedulaFront.svg';
import CedulaBackIcon from '../../icons/CedulaBack.svg';
import ProfileIcon from '../../icons/Profile.svg';

const DriverForm = () => {
  const controller = useDriverFormController();

  const inputStyles = {
    borderColor: '#7E7D7D',
    _hover: { borderColor: '#7E7D7D' },
    _focus: { borderColor: '#7E7D7D', boxShadow: 'none' },
    fontSize: ['sm', 'md', 'md'],
    fontWeight: 'normal',
    bg: 'primary.900',
    color: 'white',
  };

  return (
    <VStack
      minH='100vh'
      h='auto'
      bgColor='primary.900'
      w='full'
      gap={0}
      spacing={0}
    >
      <Header />
      <Container
        maxW='container.lg'
        py={[4, 6, 8]}
        px={[3, 4, 6]}
        mt={[4, 6, 8]}
      >
        <VStack spacing={[4, 6, 8]} align='stretch'>
          <Box textAlign='center' px={[16, 24, 32]} maxW='1024px' mx='auto'>
            <Heading
              as='h1'
              fontSize='25px'
              fontWeight={700}
              color='white'
              mb='20px'
            >
              Sumate como conductor a ViajáYa
            </Heading>
            <Text fontSize='16px' color='white' lineHeight='24px'>
              Completá este formulario para unirte como conductor. Ingresá tus
              datos personales, información del vehículo y documentos
              requeridos. Recibirás un correo en tu email cuando revisemos tu
              solicitud. ¡Empezá a manejar y generá ingresos!
            </Text>
          </Box>

          <Box
            as='form'
            onSubmit={(e: React.FormEvent<HTMLFormElement>) => {
              e.preventDefault();
              controller.validateFileFields();
              controller.handleSubmit(controller.onSubmit)(e);
            }}
            style={{ width: '100%' }}
          >
            <Divider mb={6} />

            {/* Driver Information Section */}
            <Box
              bg='primary.900'
              p={[4, 5, 6]}
              borderRadius='md'
              mb={[4, 6, 8]}
              color='white'
            >
              <Box
                border='1px solid #666'
                borderLeft='4px solid #ccff00'
                borderRadius='md'
                p={[3, 4, 5]}
                mb={[4, 5, 6]}
                bg='primary.900'
                boxShadow='md'
                display='flex'
                justifyContent='space-between'
              >
                <Heading as='h2' size='md' color='white'>
                  Datos del Conductor
                </Heading>
                <Heading as='h2' size='md' color='white'>
                  1/3
                </Heading>
              </Box>
              <Grid
                templateColumns={[
                  'repeat(1, 1fr)',
                  'repeat(1, 1fr)',
                  'repeat(2, 1fr)',
                ]}
                gap={[4, 4, 6]}
              >
                <GridItem>
                  <FormControl isInvalid={!!controller.errors.dni} minH='85px'>
                    <FormLabel fontSize={['sm', 'md', 'md']}>
                      DNI <span style={{ color: 'red' }}>*</span>
                    </FormLabel>

                    <Input
                      {...inputStyles}
                      {...controller.register('dni')}
                      placeholder='Ingresá su DNI'
                      maxLength={8}
                      autoFocus
                    />
                    <FormErrorMessage>
                      {controller.errors.dni?.message}
                    </FormErrorMessage>
                  </FormControl>
                </GridItem>

                <GridItem>
                  <FormControl
                    isInvalid={!!controller.errors.phone}
                    minH='85px'
                  >
                    <FormLabel fontSize={['sm', 'md', 'md']}>
                      Teléfono <span style={{ color: 'red' }}>*</span>
                    </FormLabel>

                    <Input
                      {...controller.register('phone')}
                      placeholder='Ingresá su teléfono'
                      {...inputStyles}
                    />
                    <FormErrorMessage>
                      {controller.errors.phone?.message}
                    </FormErrorMessage>
                  </FormControl>
                </GridItem>

                <GridItem>
                  <FormControl
                    isInvalid={!!controller.errors.firstName}
                    minH='85px'
                  >
                    <FormLabel fontSize={['sm', 'md', 'md']}>
                      Nombre <span style={{ color: 'red' }}>*</span>
                    </FormLabel>
                    <Input
                      {...inputStyles}
                      {...controller.register('firstName')}
                      placeholder='Ingresá su nombre'
                      {...inputStyles}
                    />
                    <FormErrorMessage>
                      {controller.errors.firstName?.message}
                    </FormErrorMessage>
                  </FormControl>
                </GridItem>

                <GridItem>
                  <FormControl
                    isInvalid={!!controller.errors.lastName}
                    minH='85px'
                  >
                    <FormLabel fontSize={['sm', 'md', 'md']}>
                      Apellido <span style={{ color: 'red' }}>*</span>
                    </FormLabel>

                    <Input
                      {...controller.register('lastName')}
                      placeholder='Ingresá su apellido'
                      {...inputStyles}
                    />
                    <FormErrorMessage>
                      {controller.errors.lastName?.message}
                    </FormErrorMessage>
                  </FormControl>
                </GridItem>

                <GridItem>
                  <FormControl
                    isInvalid={!!controller.errors.state}
                    minH='85px'
                  >
                    <FormLabel fontSize={['sm', 'md', 'md']}>
                      Provincia <span style={{ color: 'red' }}>*</span>
                    </FormLabel>

                    <Select
                      {...controller.register('state', {
                        required: 'La provincia es obligatoria',
                      })}
                      defaultValue='Córdoba'
                      {...inputStyles}
                      placeholder='Seleccione una provincia'
                    >
                      <option value='Córdoba'>Córdoba</option>
                    </Select>
                    <FormErrorMessage>
                      {controller.errors.state?.message}
                    </FormErrorMessage>
                  </FormControl>
                </GridItem>

                <GridItem>
                  <FormControl isInvalid={!!controller.errors.city} minH='85px'>
                    <FormLabel fontSize={['sm', 'md', 'md']}>
                      Localidad <span style={{ color: 'red' }}>*</span>
                    </FormLabel>

                    <Select
                      {...controller.register('city', {
                        required: 'La localidad es obligatoria',
                      })}
                      defaultValue='Córdoba'
                      {...inputStyles}
                      placeholder='Seleccione una localidad'
                    >
                      <option value='Córdoba'>Córdoba</option>
                    </Select>
                    <FormErrorMessage>
                      {controller.errors.city?.message}
                    </FormErrorMessage>
                  </FormControl>
                </GridItem>

                <GridItem>
                  <FormControl
                    isInvalid={!!controller.errors.email}
                    minH='85px'
                  >
                    <FormLabel fontSize={['sm', 'md', 'md']}>
                      Email <span style={{ color: 'red' }}>*</span>
                    </FormLabel>

                    <Input
                      {...controller.register('email')}
                      type='email'
                      placeholder='Ingresá su email'
                      {...inputStyles}
                    />
                    <FormErrorMessage>
                      {controller.errors.email?.message}
                    </FormErrorMessage>
                  </FormControl>
                </GridItem>

                <GridItem>
                  <FormControl
                    isInvalid={!!controller.errors.birthDate}
                    minH='85px'
                  >
                    <FormLabel fontSize={['sm', 'md', 'md']}>
                      Fecha de Nacimiento{' '}
                      <span style={{ color: 'red' }}>*</span>
                    </FormLabel>

                    <DatePicker
                      selectedDate={
                        controller.getValues('birthDate')
                          ? new Date(controller.getValues('birthDate'))
                          : undefined
                      }
                      onChange={(date) => {
                        controller.setValue('birthDate', date as Date, {
                          shouldValidate: true,
                        });
                      }}
                      maxDate={controller.eighteenYearsAgo}
                      placeholder='Seleccionar fecha de nacimiento'
                    />
                    <FormErrorMessage>
                      {controller.errors.birthDate?.message}
                    </FormErrorMessage>
                  </FormControl>
                </GridItem>

                <GridItem>
                  <FormControl
                    isInvalid={!!controller.errors.gender}
                    minH='85px'
                  >
                    <FormLabel fontSize={['sm', 'md', 'md']}>
                      Género <span style={{ color: 'red' }}>*</span>
                    </FormLabel>

                    <Select
                      {...inputStyles}
                      {...controller.register('gender', {
                        required: 'El género es obligatorio',
                      })}
                    >
                      <option value='male'>Masculino</option>
                      <option value='female'>Femenino</option>
                      <option value='other'>Otro</option>
                    </Select>
                    <FormErrorMessage>
                      {controller.errors.gender?.message}
                    </FormErrorMessage>
                  </FormControl>
                </GridItem>
              </Grid>
            </Box>

            <Divider mb={6} />

            {/* Vehicle Information Section */}
            <Box
              bg='primary.900'
              p={[4, 5, 6]}
              borderRadius='md'
              boxShadow='md'
              mb={[4, 6, 8]}
              color='white'
            >
              <Box
                border='1px solid #666'
                borderLeft='4px solid #ccff00'
                borderRadius='md'
                p={[3, 4, 5]}
                mb={[4, 5, 6]}
                bg='primary.900'
                boxShadow='md'
                display='flex'
                justifyContent='space-between'
              >
                <Heading as='h2' size='md' color='white'>
                  Datos del Vehículo
                </Heading>
                <Heading as='h2' size='md' color='white'>
                  2/3
                </Heading>
              </Box>

              <Grid
                templateColumns={[
                  'repeat(1, 1fr)',
                  'repeat(1, 1fr)',
                  'repeat(2, 1fr)',
                ]}
                gap={[4, 4, 6]}
              >
                <GridItem>
                  <FormControl
                    isInvalid={!!controller.errors.licensePlate}
                    minH='85px'
                  >
                    <FormLabel fontSize={['sm', 'md', 'md']}>
                      Patente <span style={{ color: 'red' }}>*</span>
                    </FormLabel>

                    <Input
                      {...inputStyles}
                      {...controller.register('licensePlate')}
                      placeholder='Ingresá la patente'
                    />
                    <FormErrorMessage>
                      {controller.errors.licensePlate?.message}
                    </FormErrorMessage>
                  </FormControl>
                </GridItem>

                <GridItem>
                  <FormControl
                    isInvalid={!!controller.errors.vehicleType}
                    minH='85px'
                  >
                    <FormLabel fontSize={['sm', 'md', 'md']}>
                      Tipo de Vehículo <span style={{ color: 'red' }}>*</span>
                    </FormLabel>
                    <Select
                      {...controller.register('vehicleType')}
                      placeholder='Seleccione el tipo de vehículo'
                      {...inputStyles}
                    >
                      <option value='particular'>Particular</option>
                      <option value='taxi'>Taxi</option>
                      <option value='remis'>Remis</option>
                    </Select>
                    <FormErrorMessage>
                      {controller.errors.vehicleType?.message}
                    </FormErrorMessage>
                  </FormControl>
                </GridItem>

                <GridItem>
                  <FormControl
                    isInvalid={!!controller.errors.brand}
                    minH='85px'
                  >
                    <FormLabel fontSize={['sm', 'md', 'md']}>
                      Marca <span style={{ color: 'red' }}>*</span>
                    </FormLabel>
                    <Input
                      {...controller.register('brand')}
                      placeholder='Ingresá la marca'
                      {...inputStyles}
                    />
                    <FormErrorMessage>
                      {controller.errors.brand?.message}
                    </FormErrorMessage>
                  </FormControl>
                </GridItem>

                <GridItem>
                  <FormControl
                    isInvalid={!!controller.errors.model}
                    minH='85px'
                  >
                    <FormLabel fontSize={['sm', 'md', 'md']}>
                      Modelo <span style={{ color: 'red' }}>*</span>
                    </FormLabel>
                    <Input
                      {...controller.register('model')}
                      placeholder='Ingresá el modelo'
                      {...inputStyles}
                    />
                    <FormErrorMessage>
                      {controller.errors.model?.message}
                    </FormErrorMessage>
                  </FormControl>
                </GridItem>

                <GridItem>
                  <FormControl w='100%' isInvalid={!!controller.errors?.color}>
                    <FormLabel>
                      Color <span style={{ color: 'red' }}>*</span>
                    </FormLabel>
                    <ColorSelect
                      options={colorOptions}
                      onChange={(value: string) =>
                        controller.setValue('color', value)
                      }
                      value={controller.watch('color')}
                      placeholder='Seleccione un color'
                      error={!!controller.errors?.color}
                      onError={(hasError) =>
                        hasError ? null : controller.clearErrors('color')
                      }
                    />
                    <FormErrorMessage>{`${controller.errors.color?.message}`}</FormErrorMessage>
                  </FormControl>
                </GridItem>

                <GridItem>
                  <FormControl isInvalid={!!controller.errors.year} minH='85px'>
                    <FormLabel fontSize={['sm', 'md', 'md']}>
                      Año <span style={{ color: 'red' }}>*</span>
                    </FormLabel>
                    <Input
                      {...controller.register('year')}
                      type='number'
                      placeholder='Ingresá el año'
                      {...inputStyles}
                    />
                    <FormErrorMessage>
                      {controller.errors.year?.message}
                    </FormErrorMessage>
                  </FormControl>
                </GridItem>
              </Grid>
            </Box>

            <Divider mb={6} />

            {/* Documents Upload Section */}
            <Box
              bg='primary.900'
              p={[4, 5, 6]}
              borderRadius='md'
              boxShadow='md'
              mb={8}
              color='white'
            >
              <Box
                border='1px solid #666'
                borderLeft='4px solid #ccff00'
                borderRadius='md'
                p={[3, 4, 5]}
                mb={[4, 5, 6]}
                bg='primary.900'
                boxShadow='md'
                display='flex'
                justifyContent='space-between'
              >
                <Heading as='h2' size='md' color='white'>
                  Documentación
                </Heading>
                <Heading as='h2' size='md' color='white'>
                  3/3
                </Heading>
              </Box>

              <Grid
                templateColumns={['repeat(1, 1fr)', 'repeat(2, 1fr)']}
                gap={[3, 4, 5]}
              >
                <GridItem>
                  <FormControl
                    isInvalid={!!controller.errors.dniFrontPhoto}
                    minH='85px'
                  >
                    <FormLabel fontSize={['sm', 'md', 'md']}>
                      Foto de DNI - Frente{' '}
                      <span style={{ color: 'red' }}>*</span>
                    </FormLabel>
                    <CustomFileUploader
                      values={controller.watch('dniFrontPhoto') || []}
                      onChange={(files: any[]) => {
                        if (files.length > 0 && files[0].file) {
                          controller.setValue('dniFrontPhoto', files);
                          controller.clearErrors('dniFrontPhoto');
                          controller.setUploadingFiles((prev) => ({
                            ...prev,
                            dniFrontPhoto: true,
                          }));
                          controller.executeUploadFile(
                            files[0].file,
                            'dniFrontPhoto'
                          );
                        } else if (files.length === 0) {
                          controller.setValue('dniFrontPhoto', []);
                        }
                      }}
                      onDeleteItem={() => {
                        controller.setValue('dniFrontPhoto', []);
                      }}
                      placeholderIcon={DniFrontIcon}
                      disabled={false}
                      multiple={false}
                      loading={controller.uploadingFiles.dniFrontPhoto}
                      className='file-uploader'
                    />
                    <FormErrorMessage>
                      {controller.errors.dniFrontPhoto?.message}
                    </FormErrorMessage>
                  </FormControl>
                </GridItem>

                <GridItem>
                  <FormControl
                    isInvalid={!!controller.errors.dniBackPhoto}
                    minH='85px'
                  >
                    <FormLabel fontSize={['sm', 'md', 'md']}>
                      Foto de DNI - Dorso{' '}
                      <span style={{ color: 'red' }}>*</span>
                    </FormLabel>
                    <CustomFileUploader
                      values={controller.watch('dniBackPhoto') || []}
                      onChange={(files: any[]) => {
                        if (files.length > 0 && files[0].file) {
                          controller.setValue('dniBackPhoto', files);
                          controller.clearErrors('dniBackPhoto');
                          controller.setUploadingFiles((prev) => ({
                            ...prev,
                            dniBackPhoto: true,
                          }));
                          controller.executeUploadFile(
                            files[0].file,
                            'dniBackPhoto'
                          );
                        } else if (files.length === 0) {
                          controller.setValue('dniBackPhoto', []);
                        }
                      }}
                      onDeleteItem={() => {
                        controller.setValue('dniBackPhoto', []);
                      }}
                      placeholderIcon={DniBackIcon}
                      disabled={false}
                      multiple={false}
                      loading={controller.uploadingFiles.dniBackPhoto}
                      className='file-uploader'
                    />
                    <FormErrorMessage>
                      {controller.errors.dniBackPhoto?.message}
                    </FormErrorMessage>
                  </FormControl>
                </GridItem>

                <GridItem>
                  <FormControl
                    isInvalid={!!controller.errors.licenseFrontPhoto}
                    minH='85px'
                  >
                    <FormLabel fontSize={['sm', 'md', 'md']}>
                      Foto de Licencia - Frente{' '}
                      <span style={{ color: 'red' }}>*</span>
                    </FormLabel>
                    <CustomFileUploader
                      values={controller.watch('licenseFrontPhoto') || []}
                      onChange={(files: any[]) => {
                        if (files.length > 0 && files[0].file) {
                          controller.setValue('licenseFrontPhoto', files);
                          controller.clearErrors('licenseFrontPhoto');
                          controller.setUploadingFiles((prev) => ({
                            ...prev,
                            licenseFrontPhoto: true,
                          }));
                          controller.executeUploadFile(
                            files[0].file,
                            'licenseFrontPhoto'
                          );
                        } else if (files.length === 0) {
                          controller.setValue('licenseFrontPhoto', []);
                        }
                      }}
                      onDeleteItem={() => {
                        controller.setValue('licenseFrontPhoto', []);
                      }}
                      placeholderIcon={LicenseFrontIcon}
                      disabled={false}
                      multiple={false}
                      loading={controller.uploadingFiles.licenseFrontPhoto}
                      className='file-uploader'
                    />
                    <FormErrorMessage>
                      {controller.errors.licenseFrontPhoto?.message}
                    </FormErrorMessage>
                  </FormControl>
                </GridItem>

                <GridItem>
                  <FormControl
                    isInvalid={!!controller.errors.licenseBackPhoto}
                    minH='85px'
                  >
                    <FormLabel fontSize={['sm', 'md', 'md']}>
                      Foto de Licencia - Dorso{' '}
                      <span style={{ color: 'red' }}>*</span>
                    </FormLabel>
                    <CustomFileUploader
                      values={controller.watch('licenseBackPhoto') || []}
                      onChange={(files: any[]) => {
                        if (files.length > 0 && files[0].file) {
                          controller.setValue('licenseBackPhoto', files);
                          controller.clearErrors('licenseBackPhoto');
                          controller.setUploadingFiles((prev) => ({
                            ...prev,
                            licenseBackPhoto: true,
                          }));
                          controller.executeUploadFile(
                            files[0].file,
                            'licenseBackPhoto'
                          );
                        } else if (files.length === 0) {
                          controller.setValue('licenseBackPhoto', []);
                        }
                      }}
                      onDeleteItem={() => {
                        controller.setValue('licenseBackPhoto', []);
                      }}
                      placeholderIcon={LicenseBackIcon}
                      disabled={false}
                      multiple={false}
                      loading={controller.uploadingFiles.licenseBackPhoto}
                      className='file-uploader'
                    />
                    <FormErrorMessage>
                      {controller.errors.licenseBackPhoto?.message}
                    </FormErrorMessage>
                  </FormControl>
                </GridItem>

                <GridItem>
                  <FormControl
                    isInvalid={
                      !!controller.errors.vehicleRegistrationFrontPhoto
                    }
                    minH='85px'
                  >
                    <FormLabel fontSize={['sm', 'md', 'md']}>
                      Foto de Cédula - Frente{' '}
                      <span style={{ color: 'red' }}>*</span>
                    </FormLabel>
                    <CustomFileUploader
                      values={
                        controller.watch('vehicleRegistrationFrontPhoto') || []
                      }
                      onChange={(files: any[]) => {
                        if (files.length > 0 && files[0].file) {
                          controller.setValue(
                            'vehicleRegistrationFrontPhoto',
                            files
                          );
                          controller.clearErrors(
                            'vehicleRegistrationFrontPhoto'
                          );
                          controller.setUploadingFiles((prev) => ({
                            ...prev,
                            vehicleRegistrationFrontPhoto: true,
                          }));
                          controller.executeUploadFile(
                            files[0].file,
                            'vehicleRegistrationFrontPhoto'
                          );
                        } else if (files.length === 0) {
                          controller.setValue(
                            'vehicleRegistrationFrontPhoto',
                            []
                          );
                        }
                      }}
                      onDeleteItem={() => {
                        controller.setValue(
                          'vehicleRegistrationFrontPhoto',
                          []
                        );
                      }}
                      placeholderIcon={CedulaFrontIcon}
                      disabled={false}
                      multiple={false}
                      loading={
                        controller.uploadingFiles.vehicleRegistrationFrontPhoto
                      }
                      className='file-uploader'
                    />
                    <FormErrorMessage>
                      {controller.errors.vehicleRegistrationFrontPhoto?.message}
                    </FormErrorMessage>
                  </FormControl>
                </GridItem>

                <GridItem>
                  <FormControl
                    isInvalid={!!controller.errors.vehicleRegistrationBackPhoto}
                    minH='85px'
                  >
                    <FormLabel fontSize={['sm', 'md', 'md']}>
                      Foto de Cédula - Dorso{' '}
                      <span style={{ color: 'red' }}>*</span>
                    </FormLabel>
                    <CustomFileUploader
                      values={
                        controller.watch('vehicleRegistrationBackPhoto') || []
                      }
                      onChange={(files: any[]) => {
                        if (files.length > 0 && files[0].file) {
                          controller.setValue(
                            'vehicleRegistrationBackPhoto',
                            files
                          );
                          controller.clearErrors(
                            'vehicleRegistrationBackPhoto'
                          );
                          controller.setUploadingFiles((prev) => ({
                            ...prev,
                            vehicleRegistrationBackPhoto: true,
                          }));
                          controller.executeUploadFile(
                            files[0].file,
                            'vehicleRegistrationBackPhoto'
                          );
                        } else if (files.length === 0) {
                          controller.setValue(
                            'vehicleRegistrationBackPhoto',
                            []
                          );
                        }
                      }}
                      onDeleteItem={() => {
                        controller.setValue('vehicleRegistrationBackPhoto', []);
                      }}
                      placeholderIcon={CedulaBackIcon}
                      disabled={false}
                      multiple={false}
                      loading={
                        controller.uploadingFiles.vehicleRegistrationBackPhoto
                      }
                      className='file-uploader'
                    />
                    <FormErrorMessage>
                      {controller.errors.vehicleRegistrationBackPhoto?.message}
                    </FormErrorMessage>
                  </FormControl>
                </GridItem>

                <GridItem>
                  <FormControl
                    isInvalid={!!controller.errors.profilePhoto}
                    minH='85px'
                  >
                    <FormLabel fontSize={['sm', 'md', 'md']}>
                      Foto de Perfil <span style={{ color: 'red' }}>*</span>
                    </FormLabel>
                    <CustomFileUploader
                      values={controller.watch('profilePhoto') || []}
                      onChange={(files: any[]) => {
                        if (files.length > 0 && files[0].file) {
                          controller.setValue('profilePhoto', files);
                          controller.clearErrors('profilePhoto');
                          controller.setUploadingFiles((prev) => ({
                            ...prev,
                            profilePhoto: true,
                          }));
                          controller.executeUploadFile(
                            files[0].file,
                            'profilePhoto'
                          );
                        } else if (files.length === 0) {
                          controller.setValue('profilePhoto', []);
                        }
                      }}
                      onDeleteItem={() => {
                        controller.setValue('profilePhoto', []);
                      }}
                      placeholderIcon={ProfileIcon}
                      disabled={false}
                      multiple={false}
                      loading={controller.uploadingFiles.profilePhoto}
                      className='file-uploader'
                    />
                    <FormErrorMessage>
                      {controller.errors.profilePhoto?.message}
                    </FormErrorMessage>
                  </FormControl>
                </GridItem>
              </Grid>
            </Box>

            <Divider mb={6} />

            <HStack justifyContent='center' mt={[6, 7, 10]}>
              <Button
                type='submit'
                bgColor='white'
                color='primary.800'
                size={['md', 'lg', 'lg']}
                isLoading={controller.loadingSubmit}
                isDisabled={controller.loadingSubmit}
                loadingText='Registrando...'
              >
                Registrar Conductor
              </Button>
            </HStack>
          </Box>
        </VStack>
      </Container>
      <Footer hasSectionBtn={false} />
    </VStack>
  );
};

export default DriverForm;
